import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TitleIcon from "@mui/icons-material/Title";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import HeightIcon from "@mui/icons-material/Height";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "services/api";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const makeId = () => Math.random().toString(36).slice(2, 10);

const withIds = (blocks) =>
  (Array.isArray(blocks) ? blocks : []).map((b) => ({ ...b, _id: b._id || makeId() }));

const stripIds = (blocks) => blocks.map(({ _id, ...rest }) => rest);

const tryParseBlocks = (json) => {
  try {
    if (!json) return [];
    const arr = JSON.parse(json);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const applyVars = (text, vars) => {
  let result = String(text || "");
  for (const [k, v] of Object.entries(vars || {})) {
    result = result.replace(new RegExp(`\\{${k}\\}`, "g"), v);
  }
  return result;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const BLOCK_TYPE_DEFS = [
  { type: "heading",  label: "Heading",   Icon: TitleIcon,            desc: "Bold heading text" },
  { type: "text",     label: "Paragraph", Icon: FormatAlignLeftIcon,  desc: "Regular paragraph" },
  { type: "button",   label: "Button",    Icon: TouchAppIcon,         desc: "Action or custom-URL button" },
  { type: "divider",  label: "Divider",   Icon: HorizontalRuleIcon,   desc: "Horizontal separator line" },
  { type: "spacer",   label: "Spacer",    Icon: HeightIcon,           desc: "Blank vertical space" },
  { type: "info_row", label: "Info Row",  Icon: InfoOutlinedIcon,     desc: "Label : Value display row" },
];

const BLOCK_DEFAULTS = {
  heading:  { type: "heading",  content: "" },
  text:     { type: "text",     content: "" },
  button:   { type: "button",   label: "",  linkType: "action", customUrl: "" },
  divider:  { type: "divider" },
  spacer:   { type: "spacer",   height: 16 },
  info_row: { type: "info_row", label: "",  value: "" },
};

const TEMPLATE_META = {
  linguist_invitation: {
    label: "Linguist Invitation",
    description:
      "Sent when a linguist is invited to the portal for the first time, or when an invitation is resent.",
    varsHint:
      "Use {name} for the linguist\u2019s name. A temporary password and portal login link are automatically appended after the last block.",
    actionLabel: "Password setup link (auto-generated per linguist)",
    hasActionUrl: true,
    previewExtras: [
      { label: "Temporary password:", value: "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" },
      { label: "Portal login:", value: "https://portal.example.com/linguist-login" },
    ],
  },
  password_reset: {
    label: "Password Reset",
    description: "Sent when a linguist uses the \u201cForgot Password\u201d option on the portal login page.",
    varsHint:
      "Use {name} for the linguist\u2019s name. A button with \u201cTemplate action\u201d link type will use the Firebase-generated reset URL.",
    actionLabel: "Firebase password reset link (auto-generated)",
    hasActionUrl: true,
    previewExtras: [],
  },
  email_change: {
    label: "Email Changed",
    description:
      "Sent to a linguist\u2019s new email address when their portal account email is updated.",
    varsHint:
      "Use {name} for the linguist\u2019s name. Use {newEmail} in a Paragraph or Info Row to display the new address.",
    actionLabel: null,
    hasActionUrl: false,
    previewExtras: [],
  },
  password_changed: {
    label: "Password Changed",
    description: "Sent to a linguist after their portal account password is successfully changed.",
    varsHint: "Use {name} for the linguist\u2019s name.",
    actionLabel: null,
    hasActionUrl: false,
    previewExtras: [],
  },
  assignment_offered: {
    label: "Assignment Offered",
    description: "Sent to a linguist when they are offered a new service assignment.",
    varsHint:
      "Use {name} for the linguist\u2019s name. A button with \u201cTemplate action\u201d link type will link to the linguist portal login.",
    actionLabel: "Linguist portal login URL",
    hasActionUrl: true,
    previewExtras: [],
  },
  assignment_offer_reminder: {
    label: "Assignment Offer Reminder",
    description: "Sent to a linguist as a reminder about a new service assignment.",
    varsHint:
      "Use {name} for the linguist\u2019s name. A button with \u201cTemplate action\u201d link type will link to the linguist portal login.",
    actionLabel: "Linguist portal login URL",
    hasActionUrl: true,
    previewExtras: [],
  },
};

const TEMPLATE_KEYS_ORDERED = [
  "linguist_invitation",
  "password_reset",
  "email_change",
  "password_changed",
  "assignment_offered",
  "assignment_offer_reminder",
];

const PREVIEW_DEFAULTS = {
  headerColor: "#0796A6",
  buttonColor: "#0796A6",
  portalName: "Communication Essentials Portal",
  supportEmail: "support@communicationessentialsllc.com",
};

// ---------------------------------------------------------------------------
// Block type palette
// ---------------------------------------------------------------------------
const BlockTypePalette = ({ onSelect, onClose }) => (
  <Paper
    elevation={8}
    sx={{
      p: 1.5,
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 0.75,
      width: 306,
      mt: 0.5,
    }}
  >
    {BLOCK_TYPE_DEFS.map(({ type, label, Icon, desc }) => (
      <Button
        key={type}
        onClick={() => {
          onSelect(type);
          onClose();
        }}
        title={desc}
        sx={{
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          py: 1,
          px: 0.5,
          borderRadius: "8px",
          textTransform: "none",
          color: "text.primary",
        }}
      >
        <Icon sx={{ fontSize: 22 }} />
        <Typography variant="caption" fontWeight={600} lineHeight={1.2}>
          {label}
        </Typography>
      </Button>
    ))}
    <Button
      onClick={onClose}
      size="small"
      sx={{ gridColumn: "span 3", textTransform: "none", color: "text.secondary", mt: 0.25 }}
    >
      Cancel
    </Button>
  </Paper>
);

// ---------------------------------------------------------------------------
// Single block editor
// ---------------------------------------------------------------------------
const BlockEditor = ({
  block,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  dragHandleProps,
  hasActionUrl,
  actionLabel,
}) => {
  const theme = useTheme();
  const bc = (v) => `${String(v ?? "").trim() ? theme.palette.primary.main : theme.palette.grey[500]} !important`;
  const typeDef = BLOCK_TYPE_DEFS.find((d) => d.type === block.type);
  const Icon = typeDef?.Icon || InfoOutlinedIcon;

  return (
    <Card variant="outlined" sx={{ borderRadius: "10px", mb: 1 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", p: 1.5, gap: 1 }}>
        {/* Drag handle */}
        <Box
          {...dragHandleProps}
          sx={{
            cursor: "grab",
            color: "text.disabled",
            mt: 0.75,
            flexShrink: 0,
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>

        {/* Block fields */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Chip
            icon={<Icon sx={{ fontSize: "14px !important" }} />}
            label={typeDef?.label || block.type}
            size="small"
            sx={{ mb: 1, height: 22, fontSize: 11, borderRadius: "6px" }}
          />

          {/* heading */}
          {block.type === "heading" && (
            <TextField
              value={block.content || ""}
              onChange={(e) => onChange({ ...block, content: e.target.value })}
              placeholder="Heading text\u2026"
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontWeight: 700,
                  fontSize: 15,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: bc(block.content) },
                },
              }}
            />
          )}

          {/* paragraph */}
          {block.type === "text" && (
            <TextField
              value={block.content || ""}
              onChange={(e) => onChange({ ...block, content: e.target.value })}
              placeholder="Paragraph text\u2026 Use {name} to insert the linguist\u2019s name."
              fullWidth
              multiline
              rows={2}
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: bc(block.content) } } }}
            />
          )}

          {/* button */}
          {block.type === "button" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                value={block.label || ""}
                onChange={(e) => onChange({ ...block, label: e.target.value })}
                placeholder="Button label"
                fullWidth
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: bc(block.label) } } }}
              />
              <RadioGroup
                value={block.linkType || "action"}
                onChange={(e) => onChange({ ...block, linkType: e.target.value })}
                row
              >
                <FormControlLabel
                  value="action"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="caption">
                      Template action
                      {actionLabel ? (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {" "}
                          ({actionLabel})
                        </Typography>
                      ) : null}
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio size="small" />}
                  label={<Typography variant="caption">Custom URL</Typography>}
                />
              </RadioGroup>
              {!hasActionUrl && (block.linkType === "action" || !block.linkType) && (
                <Alert severity="warning" sx={{ py: 0.25, fontSize: 12, borderRadius: "8px" }}>
                  This template has no action URL. Switch to a custom URL or remove this button.
                </Alert>
              )}
              {block.linkType === "custom" && (
                <TextField
                  value={block.customUrl || ""}
                  onChange={(e) => onChange({ ...block, customUrl: e.target.value })}
                  placeholder="https://\u2026"
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: bc(block.customUrl) } } }}
                />
              )}
            </Box>
          )}

          {/* divider — no editable fields */}
          {block.type === "divider" && (
            <Box sx={{ px: 1, py: 0.75 }}>
              <Divider />
            </Box>
          )}

          {/* spacer */}
          {block.type === "spacer" && (
            <TextField
              type="number"
              value={block.height || 16}
              onChange={(e) =>
                onChange({ ...block, height: Math.max(4, Math.min(80, Number(e.target.value))) })
              }
              label="Height (px)"
              size="small"
              inputProps={{ min: 4, max: 80, step: 4 }}
              sx={{ width: 140, "& .MuiOutlinedInput-root": { borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: bc(block.height || 16) } } }}
            />
          )}

          {/* info_row */}
          {block.type === "info_row" && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                value={block.label || ""}
                onChange={(e) => onChange({ ...block, label: e.target.value })}
                placeholder="Label:"
                size="small"
                sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: bc(block.label) } } }}
              />
              <TextField
                value={block.value || ""}
                onChange={(e) => onChange({ ...block, value: e.target.value })}
                placeholder="Value or {var}"
                size="small"
                sx={{ flex: 2, "& .MuiOutlinedInput-root": { borderRadius: "8px", "& .MuiOutlinedInput-notchedOutline": { borderColor: bc(block.value) } } }}
              />
            </Box>
          )}
        </Box>

        {/* Move / delete */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, flexShrink: 0 }}>
          <IconButton size="small" onClick={onMoveUp} disabled={!canMoveUp} sx={{ p: 0.5 }}>
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onMoveDown} disabled={!canMoveDown} sx={{ p: 0.5 }}>
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onDelete}
            color="error"
            sx={{ p: 0.5, mt: 0.5 }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Live preview
// ---------------------------------------------------------------------------
const EmailPreview = ({ form, blocks, meta, theme }) => {
  const supportEmail = form.supportEmail || PREVIEW_DEFAULTS.supportEmail;
  const portalName   = form.portalName   || PREVIEW_DEFAULTS.portalName;
  const headerColor  = form.headerColor  || PREVIEW_DEFAULTS.headerColor;
  const buttonColor  = form.buttonColor  || PREVIEW_DEFAULTS.buttonColor;
  const greeting     = form.emailGreeting  || "Hi {name},";
  const footer       = form.emailFooterText || "Need help? Contact us at {supportEmail}.";

  const pvars = { name: "Jane Doe", supportEmail, newEmail: "new.address@example.com" };
  const pv = (t) => applyVars(t, pvars);

  const border = theme?.palette?.primary?.border2 || "#dce3ef";

  const renderBlock = (block, idx) => {
    switch (block.type) {
      case "heading":
        return (
          <Typography
            key={idx}
            sx={{ fontWeight: 700, fontSize: 16, mb: 1.25, color: "#1d2433", fontFamily: "Arial, sans-serif" }}
          >
            {pv(block.content) || <em style={{ opacity: 0.4 }}>Heading text</em>}
          </Typography>
        );
      case "text":
        return (
          <Typography
            key={idx}
            sx={{ fontSize: 13, mb: 1, lineHeight: 1.65, color: "#3d4a5c", fontFamily: "Arial, sans-serif" }}
          >
            {pv(block.content) || <em style={{ opacity: 0.4 }}>Paragraph text</em>}
          </Typography>
        );
      case "button": {
        const label = pv(block.label) || "Button";
        return (
          <Box key={idx} sx={{ my: 1.5 }}>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                px: 2.5,
                py: 1,
                background: buttonColor,
                color: "#fff",
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: 13,
                fontFamily: "Arial, sans-serif",
              }}
            >
              {label}
            </Box>
          </Box>
        );
      }
      case "divider":
        return <Divider key={idx} sx={{ my: 1.5 }} />;
      case "spacer":
        return <Box key={idx} sx={{ height: `${block.height || 16}px` }} />;
      case "info_row":
        return (
          <Typography
            key={idx}
            sx={{ fontSize: 12, color: "#5a6477", mb: 0.75, fontFamily: "Arial, sans-serif" }}
          >
            <strong>{pv(block.label)}</strong>{" "}
            {pv(block.value) || <em style={{ opacity: 0.4 }}>value</em>}
          </Typography>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block" }}>
        <strong>Subject:</strong>{" "}
        {form.emailSubject || "Message from the CE Portal"}
      </Typography>
      <Box
        sx={{
          borderRadius: "14px",
          overflow: "hidden",
          border: `1px solid ${border}`,
          background: "#f4f6fb",
          fontFamily: "Arial, sans-serif",
        }}
      >
        {/* Header */}
        <Box sx={{ background: headerColor, px: 2.5, py: 1.5 }}>
          <Typography
            sx={{ color: "#fff", fontWeight: 700, fontSize: 15, fontFamily: "Arial, sans-serif" }}
          >
            {portalName}
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ background: "#fff", px: 2.5, py: 2.5 }}>
          <Typography sx={{ fontSize: 14, mb: 1.5, fontFamily: "Arial, sans-serif" }}>
            {pv(greeting)}
          </Typography>

          {blocks.length === 0 && (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ fontStyle: "italic", mb: 1 }}
            >
              No content blocks yet.
            </Typography>
          )}

          {blocks.map((block, idx) => renderBlock(block, idx))}

          {(meta?.previewExtras || []).length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {meta.previewExtras.map(({ label, value }) => (
                <Typography
                  key={label}
                  sx={{ fontSize: 12, color: "#5a6477", mb: 0.5, fontFamily: "Arial, sans-serif" }}
                >
                  {label} <strong>{value}</strong>
                </Typography>
              ))}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ background: "#fff", borderTop: "1px solid #e8edf6", px: 2.5, py: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: "#6d7485", fontFamily: "Arial, sans-serif" }}>
            {pv(footer)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const PortalEmailTemplatesTab = ({ theme }) => {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [form, setForm] = useState({});
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const paletteRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/portalSettings/emailTemplates");
      const map = {};
      for (const t of res.data) map[t.templateKey] = t;
      setTemplates(map);
    } catch {
      setError("Failed to load email templates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Close palette on outside click
  useEffect(() => {
    if (!showPalette) return;
    const handler = (e) => {
      if (paletteRef.current && !paletteRef.current.contains(e.target)) {
        setShowPalette(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showPalette]);

  const handleSelect = (key) => {
    const t = templates[key] || {};
    setSelectedKey(key);
    setForm(t);
    setBlocks(withIds(tryParseBlocks(t.blocksJson)));
    setSavedAt(null);
    setError(null);
    setShowPalette(false);
    setShowSettings(false);
  };

  const handleBack = () => {
    setSelectedKey(null);
    setForm({});
    setBlocks([]);
    setError(null);
    setShowPalette(false);
  };

  const handleFormChange = useCallback(
    (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value })),
    [],
  );

  const handleBlockChange = useCallback((id, updated) => {
    setBlocks((prev) => prev.map((b) => (b._id === id ? { ...updated, _id: id } : b)));
  }, []);

  const handleBlockDelete = useCallback((id) => {
    setBlocks((prev) => prev.filter((b) => b._id !== id));
  }, []);

  const handleBlockAdd = useCallback((type) => {
    setBlocks((prev) => [...prev, { ...BLOCK_DEFAULTS[type], _id: makeId() }]);
  }, []);

  const handleMoveUp = useCallback((idx) => {
    if (idx === 0) return;
    setBlocks((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  }, []);

  const handleMoveDown = useCallback((idx) => {
    setBlocks((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  }, []);

  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return;
    setBlocks((prev) => {
      const next = [...prev];
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, moved);
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form, blocksJson: JSON.stringify(stripIds(blocks)) };
      const res = await api.put(`/portalSettings/emailTemplates/${selectedKey}`, payload);
      setTemplates((prev) => ({ ...prev, [selectedKey]: res.data }));
      setForm(res.data);
      setSavedAt(new Date());
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  }, [form, blocks, selectedKey]);

  const handleReset = useCallback(() => {
    const t = templates[selectedKey] || {};
    setForm(t);
    setBlocks(withIds(tryParseBlocks(t.blocksJson)));
  }, [templates, selectedKey]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  // ── EDITOR VIEW ─────────────────────────────────────────────────────────────
  if (selectedKey) {
    const meta = TEMPLATE_META[selectedKey] || {};
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          size="small"
          sx={{ mb: 2, textTransform: "none", fontWeight: 600 }}
        >
          All Templates
        </Button>

        <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 3 }}>
          {/* ── Left: editor ── */}
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {meta.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {meta.description}
              </Typography>
            </Box>

            {meta.varsHint && (
              <Alert severity="info" sx={{ py: 0.5, borderRadius: "10px" }}>
                {meta.varsHint}
              </Alert>
            )}

            {/* Collapsible template settings */}
            <Card variant="outlined" sx={{ borderRadius: "12px" }}>
              <Box
                onClick={() => setShowSettings((v) => !v)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Template Settings
                </Typography>
                {showSettings ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </Box>
              <Collapse in={showSettings}>
                <Box sx={{ px: 1.5, pb: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Divider sx={{ mb: 0.5 }} />
                  <TextField
                    label="Email Subject"
                    value={form.emailSubject || ""}
                    onChange={handleFormChange("emailSubject")}
                    fullWidth
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", "& .MuiOutlinedInput-notchedOutline": { borderColor: `${String(form.emailSubject || "").trim() ? theme.palette.primary.main : theme.palette.grey[500]} !important` } } }}
                  />
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <TextField
                      label="Portal Name"
                      value={form.portalName || ""}
                      onChange={handleFormChange("portalName")}
                      fullWidth
                      size="small"
                      placeholder={PREVIEW_DEFAULTS.portalName}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", "& .MuiOutlinedInput-notchedOutline": { borderColor: `${String(form.portalName || "").trim() ? theme.palette.primary.main : theme.palette.grey[500]} !important` } } }}
                    />
                    <TextField
                      label="Support Email"
                      value={form.supportEmail || ""}
                      onChange={handleFormChange("supportEmail")}
                      fullWidth
                      size="small"
                      placeholder={PREVIEW_DEFAULTS.supportEmail}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", "& .MuiOutlinedInput-notchedOutline": { borderColor: `${String(form.supportEmail || "").trim() ? theme.palette.primary.main : theme.palette.grey[500]} !important` } } }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <TextField
                      label="Header Color"
                      value={form.headerColor || ""}
                      onChange={handleFormChange("headerColor")}
                      size="small"
                      inputProps={{ maxLength: 10 }}
                      placeholder={PREVIEW_DEFAULTS.headerColor}
                      helperText="Hex, e.g.\u00a0#0796A6"
                      sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px", "& .MuiOutlinedInput-notchedOutline": { borderColor: `${String(form.headerColor || "").trim() ? theme.palette.primary.main : theme.palette.grey[500]} !important` } } }}
                    />
                    <TextField
                      label="Button Color"
                      value={form.buttonColor || ""}
                      onChange={handleFormChange("buttonColor")}
                      size="small"
                      inputProps={{ maxLength: 10 }}
                      placeholder={PREVIEW_DEFAULTS.buttonColor}
                      helperText="Hex, e.g.\u00a0#0796A6"
                      sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px", "& .MuiOutlinedInput-notchedOutline": { borderColor: `${String(form.buttonColor || "").trim() ? theme.palette.primary.main : theme.palette.grey[500]} !important` } } }}
                    />
                  </Box>
                </Box>
              </Collapse>
            </Card>

            {/* Greeting */}
            <TextField
              label="Greeting"
              value={form.emailGreeting || ""}
              onChange={handleFormChange("emailGreeting")}
              fullWidth
              size="small"
              placeholder="Hi {name},"
              helperText="Opening line — use {name} for the linguist\u2019s name."
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", "& .MuiOutlinedInput-notchedOutline": { borderColor: `${String(form.emailGreeting || "").trim() ? theme.palette.primary.main : theme.palette.grey[500]} !important` } } }}
            />

            {/* Content blocks */}
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                Content Blocks
              </Typography>

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="email-blocks">
                  {(provided) => (
                    <Box ref={provided.innerRef} {...provided.droppableProps}>
                      {blocks.length === 0 && (
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 3,
                            mb: 1,
                            border: "2px dashed",
                            borderColor: "divider",
                            borderRadius: "12px",
                            color: "text.disabled",
                          }}
                        >
                          <Typography variant="body2">
                            No blocks yet — add one below
                          </Typography>
                        </Box>
                      )}
                      {blocks.map((block, idx) => (
                        <Draggable key={block._id} draggableId={block._id} index={idx}>
                          {(drag) => (
                            <Box ref={drag.innerRef} {...drag.draggableProps}>
                              <BlockEditor
                                block={block}
                                onChange={(updated) => handleBlockChange(block._id, updated)}
                                onDelete={() => handleBlockDelete(block._id)}
                                onMoveUp={() => handleMoveUp(idx)}
                                onMoveDown={() => handleMoveDown(idx)}
                                canMoveUp={idx > 0}
                                canMoveDown={idx < blocks.length - 1}
                                dragHandleProps={drag.dragHandleProps}
                                hasActionUrl={meta.hasActionUrl}
                                actionLabel={meta.actionLabel}
                              />
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>

              {/* Add Block */}
              <Box sx={{ position: "relative" }} ref={paletteRef}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setShowPalette((v) => !v)}
                  variant="outlined"
                  size="small"
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    borderStyle: "dashed",
                  }}
                >
                  Add Block
                </Button>
                {showPalette && (
                  <Box sx={{ position: "absolute", zIndex: 20, top: "calc(100% + 4px)", left: 0 }}>
                    <BlockTypePalette
                      onSelect={(type) => {
                        handleBlockAdd(type);
                        setShowPalette(false);
                      }}
                      onClose={() => setShowPalette(false)}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {/* Footer */}
            <TextField
              label="Footer Text"
              value={form.emailFooterText || ""}
              onChange={handleFormChange("emailFooterText")}
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Need help? Contact us at {supportEmail}."
              helperText="Use {supportEmail} to insert the support email as a link."
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", "& .MuiOutlinedInput-notchedOutline": { borderColor: `${String(form.emailFooterText || "").trim() ? theme.palette.primary.main : theme.palette.grey[500]} !important` } } }}
            />

            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1.5, mt: 0.5, alignItems: "center" }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={saving}
                sx={{ borderRadius: "10px", textTransform: "none" }}
              >
                Discard
              </Button>
              {savedAt && (
                <Typography variant="caption" color="text.secondary">
                  Saved at {savedAt.toLocaleTimeString("en-US")}
                </Typography>
              )}
            </Box>
          </Box>

          {/* ── Right: preview ── */}
          <Box
            sx={{
              width: { xs: "100%", lg: 420 },
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Preview
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5 }}>
              Updates as you type.
            </Typography>
            <Divider />
            <Box sx={{ mt: 1 }}>
              <EmailPreview form={form} blocks={blocks} meta={meta} theme={theme} />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // ── LIST VIEW ──────────────────────────────────────────────────────────────
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>
        Email Templates
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
        Customize the emails sent from the linguist portal. Each template is tied to a specific
        portal event.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {TEMPLATE_KEYS_ORDERED.map((key) => {
          const meta = TEMPLATE_META[key] || {};
          const t = templates[key] || {};
          return (
            <Card key={key} variant="outlined" sx={{ borderRadius: "12px" }}>
              <CardContent sx={{ pb: "12px !important" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <EmailOutlinedIcon
                    sx={{ mt: 0.25, fontSize: 20, color: "primary.main", flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} variant="body1">
                      {meta.label || t.templateLabel}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {meta.description}
                    </Typography>
                    {t.updatedAt && (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        Last edited:{" "}
                        {new Date(t.updatedAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSelect(key)}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px", flexShrink: 0 }}
                  >
                    Edit
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default PortalEmailTemplatesTab;

